/**
 * This library was inspired by the rules described in this article:
 * https://medium.com/blogging-guide/how-is-medium-article-read-time-calculated-924420338a85
 *
 * We can change the rules using the constants below.
 * Default rules:
 *   - Average reading speed of 275 WPM (words per minute).
 *   - Images add an additional 12 seconds for the first image, 11 seconds for
 *     the second image, and minus an additional second for each subsequent image,
 *     through the tenth image. Any images after the tenth image are counted at
 *     three seconds.
 */
const WORDS_PER_MIN = 275;
const IMAGE_READING_TIME = 12;
const IMAGE_TAGS = ['img', 'Image'];

// Get the number of images found in a string.
function imageCount(imageTags, string) {
  const combinedImageTags = imageTags.join('|');
  const pattern = `<(${combinedImageTags})([\\w\\W]+?)[\\/]?>`;
  const reg = new RegExp(pattern, 'g');
  return (string.match(reg) || []).length;
}

// Get the image(s) reading time from a string.
function imageReadingTime(
  customImageTime = IMAGE_READING_TIME,
  tags = IMAGE_TAGS,
  string
) {
  let seconds = 0;
  const count = imageCount(tags, string);

  if (count > 10) {
    seconds = (count / 2) * (customImageTime + 3) + (count - 10) * 3;
  } else {
    seconds = (count / 2) * (2 * customImageTime + (1 - count));
  }
  return {
    time: seconds / 60,
    count,
  };
}

// Strip whitespaces from a string.
function stripWhitespace(string) {
  return string.replace(/^\s+/, '').replace(/\s+$/, '');
}

// Strip HTML tags from a string.
function stripTags(string) {
  const pattern = '<\\w+(\\s+("[^"]*"|\\\'[^\\\']*\'|[^>])+)?>|<\\/\\w+>';
  const reg = new RegExp(pattern, 'gi');
  return string.replace(reg, '');
}

// Get words count from a string.
function wordsCount(string) {
  const pattern = '\\w+';
  const reg = new RegExp(pattern, 'g');
  return (string.match(reg) || []).length;
}

// Get words reading time from a string.
function wordsReadingTime(string, wordsPerMin = WORDS_PER_MIN) {
  const wordCount = wordsCount(string);
  const wordTime = wordCount / wordsPerMin;
  return {
    wordTime,
    wordCount,
  };
}

// Convert time(in minutes) to a humanized string.
function humanizeTime(time) {
  if (time < 0.5) {
    return 'less than a minute';
  }
  if (time >= 0.5 && time < 1.5) {
    return '1 minute';
  }
  return `${Math.ceil(time)} minutes`;
}

// Get the calculated reading time data from a string.
export default function readingTime(
  string,
  customWordTime,
  customImageTime,
  imageTags
) {
  const { time: imageTime, count: imageCount } = imageReadingTime(
    customImageTime,
    imageTags,
    string
  );
  const strippedString = stripTags(stripWhitespace(string));
  const { wordTime, wordCount } = wordsReadingTime(
    strippedString,
    customWordTime
  );
  return {
    humanizedDuration: humanizeTime(imageTime + wordTime),
    duration: imageTime + wordTime,
    totalWords: wordCount,
    wordTime,
    totalImages: imageCount,
    imageTime,
  };
}

// Get the humanized reading time from a string.
// Eg: 4 minutes, 1 minute, less than a minute
export function humanizedReadingTime(content) {
  return readingTime(content).humanizedDuration;
}

// Get the fixed reading time (minutes) from a string.
// Eg: 4, 1, 0
export function fixedReadingTime(content) {
  const readingMinutes = parseInt(readingTime(content).duration.toFixed());
  return readingMinutes === 0 ? 1 : readingMinutes;
}
